import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismMessage, PrismTable } from '@mylighthouse/prism-ember';
import type {
  CustomEmberTableColumn,
  CustomEmberTableRow,
} from '@mylighthouse/prism-ember/types/components/prism/table';
import { t } from 'ember-intl';
import TdName from 'lumen/components/hotel/net-revenue/cost-drilldown-section/td-name';
import TdVariance from 'lumen/components/hotel/net-revenue/cost-drilldown-section/td-variance';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import {
  COST_BREAKDOWN_UNMAPPED_COPY,
  type CostBreakdown,
  hasCostBreakdownContent,
} from 'lumen/utils/net-revenue/cost-breakdowns';
import {
  COST_COLUMN_KEYS,
  type CostColumnKey,
  DEFAULT_COLUMN_ORDER,
} from 'lumen/utils/net-revenue/cost-columns';
import { calculateAdr } from 'lumen/utils/net-revenue/metrics';
import type {
  CostBreakdownData,
  CostBreakdownTotals,
  CostTypeComparison,
  CostTypeRow,
} from 'lumen/utils/net-revenue/types';
import { deltaColorClass } from 'lumen/utils/net-revenue/variance';

import styles from './cost-drilldown-section.module.css';

interface CostRowDisplay {
  delta: string;
  deltaColor: string;
  formatted: string;
}

/**
 * Formats a metric value. The optional `signDisplay` is forwarded to
 * `Intl.NumberFormat` so delta values can show a locale-correct +/− sign
 * while the plain value omits it.
 */
type MetricFormatter = (
  value: number,
  signDisplay?: Intl.NumberFormatOptions['signDisplay'],
) => string;

interface CostRow extends CustomEmberTableRow {
  adr: number | null;
  adrDisplay: CostRowDisplay;
  avgCost: number | null;
  avgCostDisplay: CostRowDisplay;
  children?: CostRow[];
  cost: number | null;
  costDisplay: CostRowDisplay;
  costPercent: number | null;
  costPercentDisplay: CostRowDisplay;
  hasComparison: boolean;
  isCollapsed?: boolean;
  name: string | null;
  nameFormatted: string;
  nameTooltip?: string;
  netAdr: number | null;
  netAdrDisplay: CostRowDisplay;
  netRevenue: number | null;
  netRevenueDisplay: CostRowDisplay;
  netRevenuePercent: number | null;
  netRevenuePercentDisplay: CostRowDisplay;
  roomNights: number | null;
  roomNightsDisplay: CostRowDisplay;
  roomRevenue: number | null;
  roomRevenueDisplay: CostRowDisplay;
}

interface HotelNetRevenueCostDrilldownSectionSignature {
  Args: {
    breakdown: CostBreakdown;
    columnOrder?: CostColumnKey[];
    data: CostBreakdownData | undefined;
    hiddenColumns?: CostColumnKey[];
    isError?: boolean;
    isLoading?: boolean;
    nameColumnLabel: string;
  };
}

interface TableSort {
  isAscending: boolean;
  valuePath: string;
}

export default class HotelNetRevenueCostDrilldownSection extends Component<HotelNetRevenueCostDrilldownSectionSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;

  @service declare intl: Services['intl'];

  // Starts empty so rows keep the BE order until the user sorts.
  @tracked sorts: TableSort[] = [];

  get columns(): CustomEmberTableColumn[] {
    const nameColumn: CustomEmberTableColumn = {
      name: this.args.nameColumnLabel,
      valuePath: 'name',
      displayValuePath: 'nameFormatted',
      tdComponent: TdName,
      thClassNames: 'text-200--bold',
      tdClassNames: 'net-revenue-td-name',
      width: 200,
      textAlign: 'left',
    };

    const configurableColumns: Record<CostColumnKey, CustomEmberTableColumn> = {
      roomNights: {
        name: this.intl.t('general.room_nights'),
        valuePath: 'roomNights',
        displayValuePath: 'roomNightsDisplay.formatted',
        tdComponent: TdVariance,
        displayKey: 'roomNightsDisplay',
        tdClassNames: 'net-revenue-td-phantom-padding',
        width: 120,
        textAlign: 'right',
      },
      adr: {
        name: this.intl.t('general.adr'),
        valuePath: 'adr',
        displayValuePath: 'adrDisplay.formatted',
        tdComponent: TdVariance,
        displayKey: 'adrDisplay',
        tdClassNames: 'net-revenue-td-phantom-padding',
        width: 120,
        textAlign: 'right',
      },
      avgCost: {
        name: this.intl.t('net_revenue.avg_cost'),
        valuePath: 'avgCost',
        displayValuePath: 'avgCostDisplay.formatted',
        tdComponent: TdVariance,
        displayKey: 'avgCostDisplay',
        tdClassNames: 'net-revenue-td-phantom-padding',
        width: 120,
        textAlign: 'right',
      },
      netAdr: {
        name: this.intl.t('net_revenue.net_adr'),
        valuePath: 'netAdr',
        displayValuePath: 'netAdrDisplay.formatted',
        tdComponent: TdVariance,
        displayKey: 'netAdrDisplay',
        tdClassNames: 'net-revenue-td-phantom-padding',
        width: 120,
        textAlign: 'right',
      },
      roomRevenue: {
        name: this.intl.t('net_revenue.room_revenue'),
        valuePath: 'roomRevenue',
        displayValuePath: 'roomRevenueDisplay.formatted',
        tdComponent: TdVariance,
        displayKey: 'roomRevenueDisplay',
        tdClassNames: 'net-revenue-td-phantom-padding',
        width: 140,
        textAlign: 'right',
      },
      netRevenue: {
        name: this.intl.t('net_revenue.net_revenue'),
        valuePath: 'netRevenue',
        displayValuePath: 'netRevenueDisplay.formatted',
        tdComponent: TdVariance,
        displayKey: 'netRevenueDisplay',
        tdClassNames: 'net-revenue-td-phantom-padding',
        width: 140,
        textAlign: 'right',
      },
      netRevenuePercent: {
        name: this.intl.t('net_revenue.net_revenue_percent'),
        valuePath: 'netRevenuePercent',
        displayValuePath: 'netRevenuePercentDisplay.formatted',
        tdComponent: TdVariance,
        displayKey: 'netRevenuePercentDisplay',
        tdClassNames: 'net-revenue-td-phantom-padding',
        width: 130,
        textAlign: 'right',
      },
      cost: {
        name: this.intl.t('net_revenue.cost'),
        valuePath: 'cost',
        displayValuePath: 'costDisplay.formatted',
        tdComponent: TdVariance,
        displayKey: 'costDisplay',
        tdClassNames: 'net-revenue-td-phantom-padding',
        width: 120,
        textAlign: 'right',
      },
      costPercent: {
        name: this.intl.t('net_revenue.cost_percent'),
        valuePath: 'costPercent',
        displayValuePath: 'costPercentDisplay.formatted',
        tdComponent: TdVariance,
        displayKey: 'costPercentDisplay',
        tdClassNames: 'net-revenue-td-phantom-padding',
        width: 100,
        textAlign: 'right',
      },
    };

    const order = this.args.columnOrder ?? DEFAULT_COLUMN_ORDER;
    const hidden = new Set(this.args.hiddenColumns ?? []);
    const visibleColumns = order
      .filter((key) => !hidden.has(key))
      .map((key) => configurableColumns[key]);

    return [nameColumn, ...visibleColumns];
  }

  get currencyCode(): string {
    return (
      this.hotelAndMarketService.hotel.revenueInsightCurrency ??
      this.hotelAndMarketService.hotel.currencyCode
    );
  }

  get footerRows(): CostRow[] {
    const { data } = this.args;
    if (!data) {
      return [];
    }
    return [this.formatTotalsRow(data.totals)];
  }

  get hasComparison(): boolean {
    return this.args.data?.comparisonLabel != null;
  }

  get isEmpty(): boolean {
    return !this.args.isLoading && !hasCostBreakdownContent(this.args.data);
  }

  get rows(): CostRow[] {
    if (this.args.isError) {
      return [];
    }

    const { data } = this.args;
    const result = (data?.rows ?? []).map((row) => this.formatRow(row));

    if (data?.unmapped) {
      result.push(this.formatUnmappedRow(data.unmapped));
    }

    return result;
  }

  /**
   * Build the variance string shown beneath a metric. Returns the signed,
   * locale-formatted absolute delta (`actual - comparison`); for non-percent
   * columns it appends the relative change in parentheses, e.g.
   * `"+€1,234 (+15%)"`, `"−€500 (−8%)"`, `"+240 (+12%)"`. Percent columns
   * return the absolute part only (`"+1.2%"`), since the relative change would
   * just repeat the same number. A zero delta drops the sign (`"€0 (0%)"`),
   * and when `comparison` is `0` the relative term is `0%` (no meaningful
   * baseline). Only called when comparison data exists.
   */
  private buildDelta(
    formatter: MetricFormatter,
    actual: number,
    comparison: number,
    isPercentColumn: boolean,
  ): string {
    const delta = actual - comparison;
    // `signDisplay: 'exceptZero'` emits the locale-correct +/− sign from a
    // single source — avoids mixing an ASCII '+' with the Unicode minus that
    // `formatNumber` produces for negatives.
    const absStr = formatter(delta, 'exceptZero');
    // Percent columns already display a percent; the relative-change
    // parenthetical would just repeat the same number.
    if (isPercentColumn) {
      return absStr;
    }
    const relRatio = comparison !== 0 ? delta / comparison : 0;
    const relStr = `(${this.intl.formatNumber(relRatio, {
      style: 'percent',
      maximumFractionDigits: 0,
      signDisplay: 'exceptZero',
    })})`;
    return `${absStr} ${relStr}`;
  }

  // eslint-disable-next-line max-params
  private buildMetric(
    source: CostTypeRow,
    comparison: CostTypeComparison | null,
    key: CostColumnKey,
    formatMetric: MetricFormatter,
    options: {
      hideVariance?: boolean;
      invertDeltaColors?: boolean;
      isPercentColumn?: boolean;
    } = {},
  ): { display: CostRowDisplay; value: number } {
    const hideVariance = options.hideVariance ?? false;
    const invertDeltaColors = options.invertDeltaColors ?? false;
    const isPercentColumn = options.isPercentColumn ?? false;
    const actualValue = source[key];
    // Net revenue % and Cost % come from each profile's fixed commission rate,
    // which doesn't vary year-over-year, so their comparison delta always
    // renders as 0%. Suppress the line for those columns instead of showing a
    // confusing "0%" — until stay-date/historical rates make it meaningful.
    const hasComparison = this.hasComparison && !hideVariance;
    const missingText = this.intl.t('general.missing');

    let deltaText = '';
    if (hasComparison) {
      deltaText = comparison
        ? this.buildDelta(
            formatMetric,
            actualValue,
            comparison[key],
            isPercentColumn,
          )
        : missingText;
    }

    // Percent columns display with 1 decimal, so deltas < 0.05% render as
    // "+0%" / "-0%" and should be neutral, not coloured.
    const neutralThreshold = isPercentColumn ? 0.0005 : 0;

    const deltaColor =
      hasComparison && comparison
        ? deltaColorClass(actualValue - comparison[key], {
            invert: invertDeltaColors,
            neutralThreshold,
          })
        : '';

    return {
      value: actualValue,
      display: {
        formatted: formatMetric(actualValue),
        delta: deltaText,
        deltaColor,
      },
    };
  }

  private formatCurrency(
    n: number,
    maxFractionDigits = 0,
    signDisplay?: Intl.NumberFormatOptions['signDisplay'],
  ): string {
    return this.intl.formatNumber(n, {
      style: 'currency',
      currency: this.currencyCode,
      currencyDisplay: 'symbol',
      minimumFractionDigits: 0,
      maximumFractionDigits: maxFractionDigits,
      signDisplay,
    });
  }

  private formatInteger(
    n: number,
    signDisplay?: Intl.NumberFormatOptions['signDisplay'],
  ): string {
    return this.intl.formatNumber(n, {
      maximumFractionDigits: 0,
      signDisplay,
    });
  }

  private formatPercent(
    n: number,
    signDisplay?: Intl.NumberFormatOptions['signDisplay'],
  ): string {
    return this.intl.formatNumber(n, {
      style: 'percent',
      maximumFractionDigits: 1,
      signDisplay,
    });
  }

  private formatRow(source: CostTypeRow): CostRow {
    const { comparison } = source;
    const formatAsCurrency: MetricFormatter = (value, signDisplay) =>
      this.formatCurrency(value, 0, signDisplay);
    const formatAsCurrencyWith2Decimals: MetricFormatter = (
      value,
      signDisplay,
    ) => this.formatCurrency(value, 2, signDisplay);
    const formatAsInteger: MetricFormatter = (value, signDisplay) =>
      this.formatInteger(value, signDisplay);
    const formatAsPercent: MetricFormatter = (value, signDisplay) =>
      this.formatPercent(value, signDisplay);

    const { value: roomNights, display: roomNightsDisplay } = this.buildMetric(
      source,
      comparison,
      COST_COLUMN_KEYS.ROOM_NIGHTS,
      formatAsInteger,
    );
    // Nested rows can have no room nights, so a ratio would read €0.00.
    // Blank it, delta included. If only the comparison has none, its ratio is
    // a placeholder 0, so blank just the delta.
    const { value: adr, display: adrDisplay } = this.missingMetricWhenZero(
      this.buildMetric(
        source,
        comparison,
        COST_COLUMN_KEYS.ADR,
        formatAsCurrencyWith2Decimals,
      ),
      source.roomNights,
      comparison?.roomNights,
    );
    const { value: netAdr, display: netAdrDisplay } =
      this.missingMetricWhenZero(
        this.buildMetric(
          source,
          comparison,
          COST_COLUMN_KEYS.NET_ADR,
          formatAsCurrencyWith2Decimals,
        ),
        source.roomNights,
        comparison?.roomNights,
      );
    const { value: roomRevenue, display: roomRevenueDisplay } =
      this.buildMetric(
        source,
        comparison,
        COST_COLUMN_KEYS.ROOM_REVENUE,
        formatAsCurrency,
      );
    const { value: netRevenue, display: netRevenueDisplay } = this.buildMetric(
      source,
      comparison,
      COST_COLUMN_KEYS.NET_REVENUE,
      formatAsCurrency,
    );
    const { value: netRevenuePercent, display: netRevenuePercentDisplay } =
      this.missingMetricWhenZero(
        this.buildMetric(
          source,
          comparison,
          COST_COLUMN_KEYS.NET_REVENUE_PERCENT,
          formatAsPercent,
          { isPercentColumn: true, hideVariance: true },
        ),
        source.roomRevenue,
      );
    // Cost columns invert delta colors — a smaller cost is "good" (green).
    const { value: cost, display: costDisplay } = this.buildMetric(
      source,
      comparison,
      COST_COLUMN_KEYS.COST,
      formatAsCurrency,
      { invertDeltaColors: true },
    );
    const { value: costPercent, display: costPercentDisplay } =
      this.missingMetricWhenZero(
        this.buildMetric(
          source,
          comparison,
          COST_COLUMN_KEYS.COST_PERCENT,
          formatAsPercent,
          {
            invertDeltaColors: true,
            isPercentColumn: true,
            hideVariance: true,
          },
        ),
        source.roomRevenue,
      );
    const { value: avgCost, display: avgCostDisplay } =
      this.missingMetricWhenZero(
        this.buildMetric(
          source,
          comparison,
          COST_COLUMN_KEYS.AVG_COST,
          formatAsCurrencyWith2Decimals,
          { invertDeltaColors: true },
        ),
        source.roomNights,
        comparison?.roomNights,
      );

    const children = source.children.map((child) => this.formatRow(child));

    return {
      children,
      // Ember Table opens rows by default, so we set this to load parent groups closed.
      isCollapsed: children.length > 0,
      name: source.name,
      nameFormatted: source.name,
      hasComparison: this.hasComparison,
      roomNights,
      roomNightsDisplay,
      adr,
      adrDisplay,
      avgCost,
      avgCostDisplay,
      netAdr,
      netAdrDisplay,
      roomRevenue,
      roomRevenueDisplay,
      netRevenue,
      netRevenueDisplay,
      netRevenuePercent,
      netRevenuePercentDisplay,
      cost,
      costDisplay,
      costPercent,
      costPercentDisplay,
    };
  }

  private formatTotalsRow(totals: CostBreakdownTotals): CostRow {
    const blankDisplay = (formatted: string): CostRowDisplay => ({
      formatted,
      delta: '',
      deltaColor: '',
    });

    return {
      className: 'text-200--bold',
      name: this.intl.t('general.total'),
      nameFormatted: this.intl.t('general.total'),
      hasComparison: false,
      roomNights: totals.roomNights,
      roomNightsDisplay: blankDisplay(this.formatInteger(totals.roomNights)),
      adr: totals.adr,
      adrDisplay: this.missingWhenZero(
        blankDisplay(this.formatCurrency(totals.adr, 2)),
        totals.roomNights,
      ),
      avgCost: totals.avgCost,
      avgCostDisplay: this.missingWhenZero(
        blankDisplay(this.formatCurrency(totals.avgCost, 2)),
        totals.roomNights,
      ),
      netAdr: totals.netAdr,
      netAdrDisplay: this.missingWhenZero(
        blankDisplay(this.formatCurrency(totals.netAdr, 2)),
        totals.roomNights,
      ),
      roomRevenue: totals.roomRevenue,
      roomRevenueDisplay: blankDisplay(this.formatCurrency(totals.roomRevenue)),
      netRevenue: totals.netRevenue,
      netRevenueDisplay: blankDisplay(this.formatCurrency(totals.netRevenue)),
      netRevenuePercent: totals.netRevenuePercent,
      netRevenuePercentDisplay: this.missingWhenZero(
        blankDisplay(this.formatPercent(totals.netRevenuePercent)),
        totals.roomRevenue,
      ),
      cost: totals.cost,
      costDisplay: blankDisplay(this.formatCurrency(totals.cost)),
      costPercent: totals.costPercent,
      costPercentDisplay: this.missingWhenZero(
        blankDisplay(this.formatPercent(totals.costPercent)),
        totals.roomRevenue,
      ),
    };
  }

  /**
   * Cost is unknown for unmapped bookings, so only room nights, revenue,
   * and ADR are filled in. Every sort value is null, ADR included, so
   * `@sortEmptyLast` keeps the row last on any sort.
   */
  private formatUnmappedRow(
    unmapped: NonNullable<CostBreakdownData['unmapped']>,
  ): CostRow {
    const copy = COST_BREAKDOWN_UNMAPPED_COPY[this.args.breakdown];
    const blank: CostRowDisplay = { formatted: '', delta: '', deltaColor: '' };
    const adr = calculateAdr(unmapped.roomRevenue, unmapped.roomNights) ?? 0;

    return {
      name: null,
      nameFormatted: this.intl.t(copy.label),
      nameTooltip: copy.tooltip ? this.intl.t(copy.tooltip) : undefined,
      hasComparison: false,
      roomNights: null,
      roomNightsDisplay: {
        ...blank,
        formatted: this.formatInteger(unmapped.roomNights),
      },
      adr: null,
      adrDisplay: {
        ...blank,
        formatted: this.formatCurrency(adr, 2),
      },
      avgCost: null,
      avgCostDisplay: blank,
      netAdr: null,
      netAdrDisplay: blank,
      roomRevenue: null,
      roomRevenueDisplay: {
        ...blank,
        formatted: this.formatCurrency(unmapped.roomRevenue),
      },
      netRevenue: null,
      netRevenueDisplay: blank,
      netRevenuePercent: null,
      netRevenuePercentDisplay: blank,
      cost: null,
      costDisplay: blank,
      costPercent: null,
      costPercentDisplay: blank,
    };
  }

  // Keeps the value but blanks the delta, which would compare against the
  // model's placeholder 0. Rows that show no delta are left alone.
  private missingDeltaWhenZero(
    display: CostRowDisplay,
    comparisonDenominator: number | undefined,
  ): CostRowDisplay {
    return comparisonDenominator === 0 && display.delta !== ''
      ? { ...display, delta: this.intl.t('general.missing'), deltaColor: '' }
      : display;
  }

  private missingMetricWhenZero(
    metric: { display: CostRowDisplay; value: number },
    denominator: number,
    comparisonDenominator?: number,
  ): { display: CostRowDisplay; value: number | null } {
    return {
      // Null sorts last, like the unmapped row.
      value: denominator > 0 ? metric.value : null,
      display: this.missingDeltaWhenZero(
        this.missingWhenZero(metric.display, denominator),
        comparisonDenominator,
      ),
    };
  }

  private missingWhenZero(
    display: CostRowDisplay,
    denominator: number,
  ): CostRowDisplay {
    return denominator > 0
      ? display
      : {
          formatted: this.intl.t('general.missing'),
          delta: '',
          deltaColor: '',
        };
  }

  @action
  updateSorts(sorts: TableSort[]): void {
    this.sorts = sorts;
  }

  <template>
    <div data-test-id="cost-drilldown-section">
      {{#if @isError}}
        <div
          class="rounded-200 bg-elevation-surface flex items-center justify-center overflow-hidden px-400 pt-800 pb-400 shadow-neutral-100"
          data-test-id="cost-drilldown-error"
        >
          <PrismMessage
            @illustration="100x100_critical"
            @title={{t "net_revenue.cost_drilldown_error"}}
          />
        </div>
      {{else if this.isEmpty}}
        <div
          class="rounded-200 bg-elevation-surface flex items-center justify-center overflow-hidden px-400 pt-800 pb-400 shadow-neutral-100"
          data-test-id="cost-drilldown-empty"
        >
          <PrismMessage
            @illustration="100x100_info"
            @title={{t "net_revenue.cost_drilldown_empty"}}
          />
        </div>
      {{else}}
        <PrismTable
          @columns={{this.columns}}
          @footerRows={{this.footerRows}}
          @isLoading={{@isLoading}}
          @onUpdateSorts={{this.updateSorts}}
          @rows={{this.rows}}
          @sortEmptyLast={{true}}
          @sorts={{this.sorts}}
          @widthConstraint="gte-container"
          class={{styles.table}}
        />
      {{/if}}
    </div>
  </template>
}
